import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Tag } from '../types';

interface TagsState {
  byId: Record<number, Tag>;
  allIds: number[];
  byPhotoId: Record<number, { tagIds: number[]; loading: boolean; error: string | null }>;
  loading: boolean;
  error: string | null;
}

const initialState: TagsState = {
  byId: {},
  allIds: [],
  byPhotoId: {},
  loading: false,
  error: null,
};

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    setTagsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setTags: (state, action: PayloadAction<Tag[]>) => {
      state.byId = {};
      state.allIds = [];
      // Defensive guard: ensure payload is an array before iterating
      // (handles edge cases where malformed/error responses slip through)
      if (Array.isArray(action.payload)) {
        action.payload.forEach((tag) => {
          state.byId[tag.id] = tag;
          state.allIds.push(tag.id);
        });
      }
      state.error = null;
    },
    addTag: (state, action: PayloadAction<Tag>) => {
      const tag = action.payload;
      state.byId[tag.id] = tag;
      if (!state.allIds.includes(tag.id)) {
        state.allIds.push(tag.id);
      }
    },
    updateTag: (state, action: PayloadAction<Tag>) => {
      const tag = action.payload;
      state.byId[tag.id] = tag;
    },
    deleteTag: (state, action: PayloadAction<number>) => {
      const tagId = action.payload;
      delete state.byId[tagId];
      state.allIds = state.allIds.filter((id) => id !== tagId);
    },
    setTagError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setPhotoTagsLoading: (state, action: PayloadAction<{ photoId: number; loading: boolean }>) => {
      const { photoId, loading } = action.payload;
      if (!state.byPhotoId[photoId]) {
        state.byPhotoId[photoId] = { tagIds: [], loading: false, error: null };
      }
      state.byPhotoId[photoId].loading = loading;
    },
    setPhotoTags: (state, action: PayloadAction<{ photoId: number; tags: Tag[] }>) => {
      const { photoId, tags } = action.payload;
      
      // Defensive guard: ensure tags is an array before iterating
      // (handles edge cases where malformed/error responses slip through)
      if (!Array.isArray(tags)) {
        state.byPhotoId[photoId] = { tagIds: [], loading: false, error: null };
        return;
      }
      
      // Add tags to global byId
      tags.forEach(tag => {
        state.byId[tag.id] = tag;
        if (!state.allIds.includes(tag.id)) {
          state.allIds.push(tag.id);
        }
      });
      
      // Set photo-specific tag IDs
      state.byPhotoId[photoId] = {
        tagIds: tags.map(t => t.id),
        loading: false,
        error: null,
      };
    },
    setPhotoTagsError: (state, action: PayloadAction<{ photoId: number; error: string }>) => {
      const { photoId, error } = action.payload;
      if (!state.byPhotoId[photoId]) {
        state.byPhotoId[photoId] = { tagIds: [], loading: false, error: null };
      }
      state.byPhotoId[photoId].error = error;
      state.byPhotoId[photoId].loading = false;
    },
    addPhotoTag: (state, action: PayloadAction<{ photoId: number; tag: Tag }>) => {
      const { photoId, tag } = action.payload;
      
      // Add tag to global byId if not exists
      if (!state.byId[tag.id]) {
        state.byId[tag.id] = tag;
        if (!state.allIds.includes(tag.id)) {
          state.allIds.push(tag.id);
        }
      }
      
      // Initialize photo tags if not exists
      if (!state.byPhotoId[photoId]) {
        state.byPhotoId[photoId] = { tagIds: [], loading: false, error: null };
      }
      
      // Add tag ID to photo if not already there
      if (!state.byPhotoId[photoId].tagIds.includes(tag.id)) {
        state.byPhotoId[photoId].tagIds.push(tag.id);
      }
    },
    removePhotoTag: (state, action: PayloadAction<{ photoId: number; tagId: number }>) => {
      const { photoId, tagId } = action.payload;
      
      if (state.byPhotoId[photoId]) {
        state.byPhotoId[photoId].tagIds = state.byPhotoId[photoId].tagIds.filter(id => id !== tagId);
      }
    },
  },
});

export const {
  setTagsLoading,
  setTags,
  addTag,
  updateTag,
  deleteTag,
  setTagError,
  setPhotoTagsLoading,
  setPhotoTags,
  setPhotoTagsError,
  addPhotoTag,
  removePhotoTag,
} = tagsSlice.actions;

export default tagsSlice.reducer;
